import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';

const INITIAL_PASSWORD_ENDPOINT = '/api/auth/initial-password';

const createInitialFormState = (token = '') => ({
  token,
  password: '',
  confirmPassword: '',
});

const normalizeErrorMessage = (error) => {
  if (!error) {
    return 'Не удалось установить пароль. Повторите попытку позже.';
  }

  const status = error?.response?.status;
  if (status === 404) {
    return 'Токен не найден или уже использован.';
  }

  if (status === 409) {
    return 'Пользователь, связанный с токеном, находится в некорректном состоянии. Обратитесь к администратору.';
  }

  const data = error?.response?.data;
  if (data) {
    if (typeof data.message === 'string' && data.message.trim().length > 0) {
      return data.message;
    }
    if (typeof data.error === 'string' && data.error.trim().length > 0) {
      return data.error;
    }
  }

  if (typeof error.message === 'string' && error.message.trim().length > 0) {
    return error.message;
  }

  return 'Не удалось установить пароль. Повторите попытку позже.';
};

export default function InitialPasswordSetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(() => createInitialFormState());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const tokenFromQuery = useMemo(() => searchParams.get('token') || '', [searchParams]);

  useEffect(() => {
    if (tokenFromQuery) {
      setForm((prev) => ({ ...prev, token: tokenFromQuery }));
    }
  }, [tokenFromQuery]);

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) {
      return;
    }

    const trimmedToken = form.token.trim();
    const trimmedPassword = form.password.trim();
    const trimmedConfirm = form.confirmPassword.trim();

    if (!trimmedToken) {
      setError('Укажите токен установки пароля.');
      return;
    }
    if (trimmedPassword.length < 8) {
      setError('Пароль должен содержать не менее 8 символов.');
      return;
    }
    if (trimmedPassword !== trimmedConfirm) {
      setError('Пароли должны совпадать.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const response = await api.post(
        INITIAL_PASSWORD_ENDPOINT,
        { token: trimmedToken, password: trimmedPassword },
        { withCredentials: true },
      );
      const message = response?.data?.message || 'Пароль успешно установлен. Теперь вы можете войти.';
      setSuccess(message);
    } catch (submissionError) {
      setSuccess(null);
      setError(normalizeErrorMessage(submissionError));
    } finally {
      setSubmitting(false);
    }
  };

  const handleNavigateToLogin = () => {
    navigate('/login');
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 5, md: 9 } }}>
      <Paper
        elevation={0}
        className="surface-glow page-enter"
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 200,
            height: 200,
            borderRadius: '50%',
            top: -100,
            left: -70,
            background: 'radial-gradient(circle, rgba(11,114,133,0.22) 0%, rgba(11,114,133,0) 68%)',
            pointerEvents: 'none',
          }}
        />
        <Stack spacing={0.5} sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary">
            Первый вход
          </Typography>
          <Typography variant="h4" component="h1">
            Установка пароля
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Введите одноразовый токен, полученный от администратора, и задайте новый пароль для доступа к системе.
          </Typography>
        </Stack>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label="Токен установки пароля"
              name="token"
              value={form.token}
              onChange={handleFieldChange('token')}
              disabled={submitting || Boolean(success)}
              autoComplete="one-time-code"
              required
              fullWidth
            />
            <TextField
              label="Новый пароль"
              name="password"
              type="password"
              value={form.password}
              onChange={handleFieldChange('password')}
              disabled={submitting || Boolean(success)}
              autoComplete="new-password"
              required
              fullWidth
              helperText="Минимум 8 символов"
            />
            <TextField
              label="Повторите пароль"
              name="confirmPassword"
              type="password"
              value={form.confirmPassword}
              onChange={handleFieldChange('confirmPassword')}
              disabled={submitting || Boolean(success)}
              autoComplete="new-password"
              required
              fullWidth
            />
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success">
                {success}
              </Alert>
            )}
            <Button
              type="submit"
              variant="contained"
              size="large"
              className="interactive-lift"
              disabled={submitting || Boolean(success)}
            >
              {submitting ? 'Сохранение…' : 'Установить пароль'}
            </Button>
            <Typography variant="body2" color="text.secondary" align="center">
              Уже есть пароль?{' '}
              <MuiLink component={Link} to="/login">
                Перейдите к странице входа
              </MuiLink>
            </Typography>
            {success && (
              <Button variant="outlined" onClick={handleNavigateToLogin}>
                Перейти ко входу
              </Button>
            )}
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
}
